import { combineSlices, configureStore, createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { baseApi } from '../api/baseApi';
import { ownedPastesSlice, pasteCreated, pasteForgotten } from '../features/owned/ownedPastesSlice';
import { loadOwnedPastes, saveOwnedPastes } from '../features/owned/persistence';
import { loadTheme, saveTheme } from '../features/ui/themePersistence';
import { initialUiState, themeChanged, uiSlice } from '../features/ui/uiSlice';

const rootReducer = combineSlices(baseApi, ownedPastesSlice, uiSlice);

export type RootState = ReturnType<typeof rootReducer>;

/** Creates a store; owned pastes are loaded from and written back to {@code storage}. */
export function makeStore(storage: Storage | undefined = safeLocalStorage()) {
  const persistence = createListenerMiddleware<RootState>();
  persistence.startListening({
    matcher: isAnyOf(pasteCreated, pasteForgotten),
    effect: (_action, api) => saveOwnedPastes(storage, api.getState().ownedPastes),
  });
  persistence.startListening({
    actionCreator: themeChanged,
    effect: (action) => saveTheme(storage, action.payload),
  });
  return configureStore({
    reducer: rootReducer,
    preloadedState: {
      ownedPastes: loadOwnedPastes(storage),
      ui: { ...initialUiState, theme: loadTheme(storage) },
    },
    middleware: (getDefault) => getDefault().prepend(persistence.middleware).concat(baseApi.middleware),
  });
}

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];

/** Accessing localStorage itself can throw (e.g. blocked site data). */
export function safeLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
